<template>
  <div>
    <div
      class="list"
      :class="{ 'list-refreshing': refreshing }"
      v-for="list in data"
      :key="list.id"
    >
      <ShoppingListHeader
        :list="list"
        :projectInfo="props.projectInfo"
        @on-delete-list="openDeleteShoppingListModal"
        @on-export-list="openExportModal"
      />
      <draggable
        v-model="list.shoppingListRooms"
        @change="
          sortableShoppingListRoom($event, list.shoppingListRooms, list.id)
        "
        item-key="id"
        handle=".handle-room"
      >
        <template #item="{ element }">
          <ShoppingListRoomItem
            :key="element.id"
            :room="element"
            :project-id="projectId"
            :project-info="props.projectInfo"
            :shopping-list-id="list.id"
            @on-delete-product="openDeleteShoppingListProductModal"
            @on-delete-room="openDeleteShoppingListRoomModal"
          />
        </template>
      </draggable>

      <AddShoppingListRoom
        v-if="props.projectInfo.myRoleInProject === Role.Architect"
        :project-id="projectId"
        :shopping-list-id="list.id"
      />
      <ShoppingListCart
        v-if="!refreshing && auth.user.role !== Role.Admin"
        :shopping-list-id="list.id"
      />
    </div>
    <DeleteShoppingListProductModal ref="deleteShoppingListProductModal" />
    <DeleteShoppingListRoomModal ref="deleteShoppingListRoomModal" />
    <ClearShoppingListModal ref="clearShoppingListModal" />
    <ExportShoppingListModal
      ref="exportShoppingListModal"
      :project-info="props.projectInfo"
      :list="exportShoppingList"
      @close="onCloseExportModal"
    />
    <Cart v-if="auth.user.role !== Role.Admin" />
  </div>
</template>

<script setup>
import draggable from "vuedraggable";
import { inject, ref } from "vue";
import { useRoute } from "vue-router";
import AddShoppingListRoom from "./shoppingList/AddShoppingListRoom.vue";
import DeleteShoppingListProductModal from "./shoppingList/DeleteShoppingListProductModal.vue";
import { useShoppingListStore } from "@/stores/shoppingList.store";
import { storeToRefs } from "pinia";
import ShoppingListRoomItem from "./shoppingList/ShoppingListRoomItem.vue";
import DeleteShoppingListRoomModal from "./shoppingList/DeleteShoppingListRoomModal.vue";
import ClearShoppingListModal from "./shoppingList/ClearShoppingListModal.vue";
import ExportShoppingListModal from "./shoppingList/ExportShoppingListModal.vue";
import ShoppingListHeader from "./shoppingList/ShoppingListHeader.vue";
import ShoppingListCart from "./shoppingList/ShoppingListCart.vue";
import Cart from "@/components/cart/Cart";

import { useAuthStore } from "@/stores/auth.store";
import { Role } from "@/helpsers/role.helper";
import axios from "axios";
import ApiHelper from "@/helpsers/api.helper";
import authHelper from "@/helpsers/auth.helper";
import AlertHelper from "@/helpsers/alert.helper";
import { useI18n } from "vue-i18n";

const auth = useAuthStore();

const props = defineProps({
  projectInfo: Object,
});

const route = useRoute();
const projectId = route.params.id;
const alert = inject("alert");
const { t } = useI18n();

const store = useShoppingListStore();

const { data, refreshing } = storeToRefs(store);

/** Delete Product */
const deleteShoppingListProductModal = ref();
function openDeleteShoppingListProductModal({
  shoppingListId,
  roomId,
  productId,
}) {
  deleteShoppingListProductModal.value?.showModal(
    projectId,
    shoppingListId,
    roomId,
    productId
  );
}
/** Delete Room */
const deleteShoppingListRoomModal = ref();
function openDeleteShoppingListRoomModal({ shoppingListId, roomId }) {
  deleteShoppingListRoomModal.value?.showModal({ shoppingListId, roomId });
}
/** Clear shopping list */
const clearShoppingListModal = ref();
function openDeleteShoppingListModal({ shoppingListId }) {
  clearShoppingListModal.value?.showModal({ shoppingListId });
}

// Export shopping list
const exportShoppingListModal = ref();
const exportShoppingList = ref(null);
function openExportModal({ shoppingList }) {
  exportShoppingList.value = shoppingList;
  exportShoppingListModal.value?.showModal({ shoppingList });
}
function onCloseExportModal() {
  exportShoppingList.value = null;
}

async function sortableShoppingListRoom(event, shoppingList, listId) {
  try {
    const ids = shoppingList.map((el) => el.id);
    await axios.put(
      ApiHelper.getShoppingListChangeOrderCategoryUrl(
        props.projectInfo.id,
        listId
      ),
      {
        roomsIds: ids,
      },
      {
        headers: authHelper.authHeader(),
      }
    );
  } catch (error) {
    AlertHelper.show(alert, t("list.fail_change_category_order"), "", "error");
  }
}
</script>

<style scoped lang="scss">
.list {
  position: relative;
  &-refreshing {
    filter: blur(2px);
  }
}
</style>
